import { useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent } from '@rocco/components/dialog';
import {
    BOOKING_AVAILABILITY_API_ROUTE,
    type BookingAvailabilityApiResponse,
} from '~/routes/remix-api.booking.availability';
import { BOOKING_BOOK_API_ROUTE } from '~/routes/remix-api.booking.book';
import { BookingScheduleControls } from './BookingScheduleControls';
import { BookingScheduleControlsMobile } from './BookingScheduleControlsMobile';
import {
    WORKDAY_END_HOUR,
    WORKDAY_INTERVAL_MINUTES,
    WORKDAY_START_HOUR,
} from '../constants';
import { useBookingSchedule } from '../hooks/use-booking-schedule';

interface ClientDirectorBookingModalProps {
    /** The Client Director CalendarSession to book against. */
    sessionId: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

function getBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'Pacific/Auckland';
    }
}

/**
 * In-page booking modal for a project's linked Client Director session. Fetches
 * availability from the booking resource route on open and drives
 * {@link BookingScheduleControls} via the shared {@link useBookingSchedule} hook,
 * so the experience matches the standalone /booking page without leaving the
 * project detail page.
 */
export const ClientDirectorBookingModal = ({
    sessionId,
    open,
    onOpenChange,
}: ClientDirectorBookingModalProps) => {
    const { mobile: isMobile } = useAppDisplayModeContext();

    // Bump a "session" counter every time the modal opens so both fetchers below
    // get a fresh key — and therefore fresh state — on each open. Without this,
    // useFetcher data persists while the modal stays mounted, so a previous
    // availability error/result or a past booking success would carry over and
    // block a retry or show a stale confirmation screen on reopen.
    const [openSession, setOpenSession] = useState(0);
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setOpenSession((session) => session + 1);
        }
        wasOpenRef.current = open;
    }, [open]);

    const availabilityFetcher = useFetcher<BookingAvailabilityApiResponse>({
        key: `booking-availability-${sessionId}-${openSession}`,
    });

    useEffect(() => {
        if (!open) return;
        if (availabilityFetcher.state !== 'idle' || availabilityFetcher.data) {
            return;
        }
        const tz = getBrowserTimezone();
        availabilityFetcher.load(
            `${BOOKING_AVAILABILITY_API_ROUTE}?sessionId=${sessionId}&timezone=${encodeURIComponent(tz)}`,
        );
    }, [open, sessionId, availabilityFetcher]);

    const availability = availabilityFetcher.data?.availability;
    const loadError = availabilityFetcher.data?.error;
    const isLoadingAvailability =
        availabilityFetcher.state !== 'idle' || (!availability && !loadError);

    const booking = useBookingSchedule({
        availability,
        serverTimezone: getBrowserTimezone(),
        actionPath: BOOKING_BOOK_API_ROUTE,
        fetcherKey: `booking-submit-${sessionId}-${openSession}`,
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] w-full overflow-y-auto p-0 sm:w-auto sm:max-w-[unset]">
                {isLoadingAvailability ? (
                    <div className="flex min-h-80 w-full items-center justify-center p-10">
                        <div className="text-center">
                            <div className="mb-6 inline-block h-16 w-16 animate-spin rounded-full border-4 border-gray-200 border-t-brick-600" />
                            <p className="text-xl font-medium text-gray-900">
                                Preparing your booking experience...
                            </p>
                        </div>
                    </div>
                ) : loadError ? (
                    <div className="flex min-h-80 w-full items-center justify-center p-10">
                        <div className="text-center">
                            <AlertTriangle className="text-rose-600 mx-auto mb-6 h-16 w-16" />
                            <p className="text-base text-gray-700">
                                {loadError.message}
                            </p>
                        </div>
                    </div>
                ) : isMobile ? (
                    <BookingScheduleControlsMobile
                        sessionTitle={availability?.sessionTitle}
                        timezoneName={booking.timezoneName}
                        onTimezoneChange={booking.handleTimezoneChange}
                        availableTimezones={booking.availableTimezones}
                        meetingType={booking.meetingType}
                        consultedMember={booking.consultedMember}
                        professional={booking.professional}
                        isProfessionalLoading={false}
                        selectedDateTimestamp={booking.selectedDateTimestamp}
                        onCalendarSelect={booking.handleCalendarSelect}
                        freeDays={booking.freeDays}
                        durations={booking.durations}
                        selectedDuration={booking.selectedDuration}
                        onDurationChange={booking.handleDurationChange}
                        isDurationDisabled={booking.isDurationDisabled}
                        freeHours={booking.freeHoursForSelectedDay}
                        selectedSlotTimestamp={booking.selectedSlotTimestamp}
                        onTimeSelect={booking.handleTimeSelectWithAssignment}
                        normalizedSelectedSlotTimestamp={
                            booking.selectedSlotTimestamp
                        }
                        selectedSlotLabel={booking.selectedSlotLabel}
                        statusMessage={booking.statusMessage}
                        success={booking.success}
                        failure={booking.failure}
                        onSubmit={({ userName, userEmail, userCompany }) => {
                            booking.handleSubmit({
                                clientName: userName,
                                clientEmail: userEmail,
                                clientCompany: userCompany,
                            });
                        }}
                        bookingDisabled={booking.bookingDisabled}
                        confirmButtonLabel="Confirm"
                        userEmailLabel="Enter your email address"
                        submissionState={booking.submissionState}
                        timePickerStartHour={WORKDAY_START_HOUR}
                        timePickerEndHour={WORKDAY_END_HOUR}
                        timePickerStepMinutes={WORKDAY_INTERVAL_MINUTES}
                        eventId={booking.bookingEventId}
                        showSpamHint={booking.showSpamHint}
                    />
                ) : (
                    <BookingScheduleControls
                        timezoneName={booking.timezoneName}
                        onTimezoneChange={booking.handleTimezoneChange}
                        availableTimezones={booking.availableTimezones}
                        meetingType={booking.meetingType}
                        consultedMember={booking.consultedMember}
                        professional={booking.professional}
                        isProfessionalLoading={false}
                        selectedDateTimestamp={booking.selectedDateTimestamp}
                        onCalendarSelect={booking.handleCalendarSelect}
                        freeDays={booking.freeDays}
                        durations={booking.durations}
                        selectedDuration={booking.selectedDuration}
                        onDurationChange={booking.setSelectedDuration}
                        isDurationDisabled={booking.isDurationDisabled}
                        freeHours={booking.freeHoursForSelectedDay}
                        selectedSlotTimestamp={booking.selectedSlotTimestamp}
                        onTimeSelect={booking.handleTimeSelectWithAssignment}
                        normalizedSelectedSlotTimestamp={
                            booking.selectedSlotTimestamp
                        }
                        selectedSlotLabel={booking.selectedSlotLabel}
                        statusMessage={booking.statusMessage}
                        success={booking.success}
                        failure={booking.failure}
                        onSubmit={booking.handleSubmit}
                        bookingDisabled={booking.bookingDisabled}
                        confirmButtonLabel="Confirm"
                        userEmailLabel="Enter your email address"
                        submissionState={booking.submissionState}
                        timePickerVariant="list"
                        timePickerStartHour={WORKDAY_START_HOUR}
                        timePickerEndHour={WORKDAY_END_HOUR}
                        timePickerStepMinutes={WORKDAY_INTERVAL_MINUTES}
                        onConfirmationPhaseChange={
                            booking.setIsConfirmationPhase
                        }
                        eventId={booking.bookingEventId}
                        showSpamHint={booking.showSpamHint}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};
